import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Check, AlertCircle, X, ArrowRight } from "lucide-react";
import { LOGO_URL } from "../components/Logo";

const API = process.env.REACT_APP_BACKEND_URL;

const fmt = (n, c = "EUR") =>
  new Intl.NumberFormat("fr-FR", { style: "currency", currency: c, maximumFractionDigits: 2 }).format(n || 0);

const dt = (s) => s ? new Date(s).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" }) : "—";

export default function ProposalPublic() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [decision, setDecision] = useState(null); // null | "accept" | "decline"
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    axios.get(`${API}/api/proposition/${token}`)
      .then((r) => setData(r.data))
      .catch((e) => setError(e?.response?.data?.detail || "Lien invalide ou expiré."));
  }, [token]);

  const send = async (which) => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/proposition/${token}`, {
        decision: which,
        note: note.trim(),
      });
      const r = await axios.get(`${API}/api/proposition/${token}`);
      setData(r.data);
      setDecision(null);
    } catch (e) {
      setError(e?.response?.data?.detail || "Erreur lors de l'envoi.");
    } finally { setSubmitting(false); }
  };

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6" style={{ background: "var(--gai-white)" }} data-testid="proposal-error">
        <div className="max-w-md text-center">
          <AlertCircle size={36} className="mx-auto text-[var(--gai-black)] opacity-70" />
          <h2 className="serif text-3xl mt-4 text-[var(--gai-black)]">Lien indisponible</h2>
          <p className="text-sm text-[rgba(5,16,36,0.7)] mt-3">{error}</p>
          <Link to="/" className="gai-btn-gold mt-6 inline-flex">Retour à l'accueil</Link>
        </div>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "var(--gai-white)" }}>
        <div className="text-[var(--gai-black)] text-sm">Chargement…</div>
      </div>
    );
  }

  const decided = data.status === "accepted" || data.status === "declined";

  return (
    <div className="min-h-screen" style={{ background: "var(--gai-white)" }} data-testid="page-proposal-public">
      <header className="border-b" style={{ borderColor: "rgba(5,16,36,0.1)" }}>
        <div className="max-w-4xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src={LOGO_URL} alt="GAI" className="w-9 h-9 object-contain" draggable="false" />
            <div>
              <div className="serif text-base text-[var(--gai-black)] leading-tight">GLOBAL AFRIQUE</div>
              <div className="text-[0.6rem] tracking-[0.22em] uppercase text-[rgba(5,16,36,0.55)]">International</div>
            </div>
          </Link>
          <div className="text-[0.6rem] tracking-[0.22em] uppercase text-[rgba(5,16,36,0.55)] hidden sm:block">
            Proposition commerciale
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-12 md:py-16">
        {/* Header / number */}
        <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <div>
            <div className="text-[0.7rem] tracking-[0.22em] uppercase text-[rgba(5,16,36,0.55)]">{data.number}</div>
            <h1 className="serif text-4xl md:text-5xl mt-2 text-[var(--gai-black)] leading-tight">{data.title}</h1>
          </div>
          <div className="text-right">
            <div className="text-[0.65rem] tracking-[0.22em] uppercase text-[rgba(5,16,36,0.55)]">Émise le</div>
            <div className="serif text-lg text-[var(--gai-black)]">{dt(data.sent_at || data.created_at)}</div>
            {data.validity_days && (
              <div className="text-[0.7rem] text-[rgba(5,16,36,0.55)] mt-1">
                Valable {data.validity_days} jours
              </div>
            )}
          </div>
        </div>

        {/* Recipient */}
        <div className="grid md:grid-cols-2 gap-6 mb-10">
          <div>
            <div className="text-[0.65rem] tracking-[0.22em] uppercase text-[rgba(5,16,36,0.55)] mb-2">Adressée à</div>
            <div className="serif text-xl text-[var(--gai-black)]">{data.client_name || data.client_email}</div>
            {data.client_company && <div className="text-sm text-[rgba(5,16,36,0.7)]">{data.client_company}</div>}
            <div className="text-sm text-[rgba(5,16,36,0.55)]">{data.client_email}</div>
          </div>
          <div>
            <div className="text-[0.65rem] tracking-[0.22em] uppercase text-[rgba(5,16,36,0.55)] mb-2">De la part de</div>
            <div className="serif text-xl text-[var(--gai-black)]">GLOBAL AFRIQUE INTERNATIONAL</div>
            <div className="text-sm text-[rgba(5,16,36,0.7)]">Direction commerciale & éditoriale</div>
          </div>
        </div>

        {data.intro_note && (
          <div className="mb-10 p-5 border-l-2 italic text-[rgba(5,16,36,0.85)] text-base leading-relaxed"
            style={{ borderColor: "var(--gai-red)", background: "var(--gai-grey-50)" }}>
            « {data.intro_note} »
          </div>
        )}

        {/* Items table */}
        <div className="border" style={{ borderColor: "rgba(5,16,36,0.15)" }} data-testid="proposal-public-items">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[0.66rem] tracking-[0.2em] uppercase" style={{ background: "rgba(5,16,36,0.04)", color: "rgba(5,16,36,0.55)" }}>
                <th className="py-3 px-5">Désignation</th>
                <th className="py-3 px-3 text-center">Qté</th>
                <th className="py-3 px-3 text-right">PU</th>
                <th className="py-3 px-5 text-right">Total</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((it, i) => (
                <tr key={i} className="border-t" style={{ borderColor: "rgba(5,16,36,0.1)" }}>
                  <td className="py-4 px-5">
                    <div className="text-[var(--gai-black)] font-medium">{it.name}</div>
                    {it.description && <div className="text-xs text-[rgba(5,16,36,0.6)] mt-1">{it.description}</div>}
                  </td>
                  <td className="py-4 px-3 text-center text-[rgba(5,16,36,0.75)]">{it.quantity}</td>
                  <td className="py-4 px-3 text-right text-[rgba(5,16,36,0.75)]">{fmt(it.unit_price, data.currency)}</td>
                  <td className="py-4 px-5 text-right font-medium text-[var(--gai-black)]">
                    {fmt((it.quantity || 0) * (it.unit_price || 0), data.currency)}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t" style={{ borderColor: "rgba(5,16,36,0.15)" }}>
                <td colSpan={3} className="py-3 px-5 text-right text-[rgba(5,16,36,0.65)]">Sous-total</td>
                <td className="py-3 px-5 text-right text-[var(--gai-black)]">{fmt(data.subtotal, data.currency)}</td>
              </tr>
              {data.discount_pct > 0 && (
                <tr>
                  <td colSpan={3} className="py-2 px-5 text-right text-[rgba(5,16,36,0.65)]">
                    Remise commerciale ({data.discount_pct}%)
                  </td>
                  <td className="py-2 px-5 text-right text-[var(--gai-black)]">- {fmt(data.discount_amount, data.currency)}</td>
                </tr>
              )}
              <tr style={{ background: "var(--gai-black)", color: "var(--gai-white)" }}>
                <td colSpan={3} className="py-4 px-5 text-right text-[0.7rem] tracking-[0.22em] uppercase">Total</td>
                <td className="py-4 px-5 text-right serif text-2xl gai-gradient-gold" data-testid="proposal-public-total">
                  {fmt(data.total, data.currency)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>

        {/* Decision */}
        <div className="mt-12">
          {decided ? (
            <div className="text-center" data-testid="proposal-decision-recap">
              <div
                className="w-16 h-16 mx-auto flex items-center justify-center"
                style={{
                  background: data.status === "accepted" ? "var(--gai-black)" : "rgba(248,113,113,0.15)",
                  color: data.status === "accepted" ? "var(--gai-red)" : "#b91c1c",
                }}
              >
                {data.status === "accepted" ? <Check size={28} /> : <X size={28} />}
              </div>
              <h2 className="serif text-3xl mt-5 text-[var(--gai-black)]">
                {data.status === "accepted" ? "Proposition acceptée" : "Proposition refusée"}
              </h2>
              <p className="mt-3 text-sm text-[rgba(5,16,36,0.6)]">
                Décision enregistrée le {dt(data.decided_at)}.
              </p>
              {data.decision_note && (
                <p className="mt-3 italic text-[rgba(5,16,36,0.7)] max-w-xl mx-auto">
                  « {data.decision_note} »
                </p>
              )}
              {data.status === "accepted" && (
                <p className="mt-6 text-sm text-[rgba(5,16,36,0.7)] max-w-lg mx-auto">
                  Notre équipe revient vers vous sous 48 h ouvrées avec les prochaines étapes (bon de commande, calendrier de production, mise en ligne).
                </p>
              )}
              <Link to="/" className="gai-btn-gold mt-8 inline-flex">
                Retour sur GAI <ArrowRight size={14} />
              </Link>
            </div>
          ) : (
            <div data-testid="proposal-decision-actions">
              <div className="text-[0.65rem] tracking-[0.22em] uppercase text-[rgba(5,16,36,0.55)] mb-3">Votre décision</div>

              {decision && (
                <div className="border p-5 mb-5" style={{ borderColor: "rgba(5,16,36,0.15)" }}>
                  <div className="serif text-xl text-[var(--gai-black)]">
                    {decision === "accept" ? "Confirmer l'acceptation" : "Décliner cette proposition"}
                  </div>
                  <textarea rows={3} className="w-full mt-3 p-3 bg-transparent border outline-none focus:border-[var(--gai-red)] resize-none text-[var(--gai-black)]"
                    style={{ borderColor: "rgba(5,16,36,0.25)" }}
                    placeholder={decision === "accept" ? "Commentaire éventuel (optionnel)" : "Pouvez-vous nous indiquer la raison ? (optionnel)"}
                    value={note} onChange={(e) => setNote(e.target.value)}
                    data-testid="decision-note" />
                  <div className="mt-3 flex justify-end gap-3">
                    <button onClick={() => { setDecision(null); setNote(""); }}
                      className="px-4 py-3 text-xs tracking-[0.18em] uppercase border" style={{ borderColor: "rgba(5,16,36,0.2)", color: "rgba(5,16,36,0.7)" }}>
                      Annuler
                    </button>
                    <button onClick={() => send(decision === "accept" ? "accepted" : "declined")}
                      disabled={submitting}
                      className="px-6 py-3 text-xs tracking-[0.18em] uppercase font-semibold transition disabled:opacity-60"
                      style={{
                        background: decision === "accept" ? "var(--gai-black)" : "#b91c1c",
                        color: "var(--gai-white)",
                      }}
                      data-testid="decision-confirm">
                      {submitting ? "Envoi…" : (decision === "accept" ? "Confirmer l'acceptation" : "Confirmer le refus")}
                    </button>
                  </div>
                </div>
              )}

              <div className="flex flex-wrap gap-3">
                <button onClick={() => setDecision("accept")}
                  className="px-7 py-4 text-xs tracking-[0.18em] uppercase font-semibold transition"
                  style={{ background: "var(--gai-red)", color: "var(--gai-white)" }}
                  data-testid="proposal-accept-btn">
                  <Check size={13} className="inline mr-2" /> Accepter la proposition
                </button>
                <button onClick={() => setDecision("decline")}
                  className="px-7 py-4 text-xs tracking-[0.18em] uppercase border"
                  style={{ borderColor: "rgba(5,16,36,0.3)", color: "rgba(5,16,36,0.75)" }}
                  data-testid="proposal-decline-btn">
                  Décliner
                </button>
              </div>
            </div>
          )}
        </div>
      </main>

      <footer className="border-t mt-16 py-6" style={{ borderColor: "rgba(5,16,36,0.1)" }}>
        <div className="max-w-4xl mx-auto px-6 text-[0.65rem] tracking-[0.22em] uppercase text-[rgba(5,16,36,0.5)] text-center">
          GLOBAL AFRIQUE INTERNATIONAL · Document confidentiel
        </div>
      </footer>
    </div>
  );
}
